import { Page, Text, View, Document, Image } from '@react-pdf/renderer';
import { DateTime } from 'luxon';
import React from 'react';

import { PQFLAnswer } from '../../page';
import { getAnswerValue } from '../content-wrapper/utils';
import { formatISODate } from '../table';
import { styles } from './styled';

interface IPQFLDocumentProps {
  base64Data?: string;
  assinatura_produtor?: string;
  assinatura_tecnico?: string;
  username?: string;
  qualityItems?: {
    DFSIGLAANALISE: string;
    DFMEDIA: number;
    DFUNIDADE?: string;
  }[];
  company: {
    legalName: string;
  };
  property: {
    name: string;
    producerName: string;
    municipalityName: string;
  };
  endDate: string | null;
  status?: string;
  answers: PQFLAnswer[];
  technicalRecommendation?: string | null;
}
const NO_ISSUES_STATUS = 'Inconformidades não encontradas';

const removeZeroToTheLeft = (value: string | number | null | undefined) => {
  return value ? String(value).replace(/^0+/, '') : '';
};

export const PQFLDocument: React.FC<IPQFLDocumentProps> = ({
  base64Data,
  username,
  company,
  property,
  endDate,
  status,
  answers,
  technicalRecommendation,
  assinatura_produtor,
  assinatura_tecnico,
  qualityItems = [],
}) => {
  // remove itens da Qualidade onde a média é nula ou indefinida, para que apareça no PDF apenas
  // as siglas que contém valores válidos
  const validQualityItems = qualityItems.filter(
    item => item.DFMEDIA !== null && item.DFMEDIA !== undefined,
  );
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <View style={styles.logoContainer}>
            {base64Data && <Image src={`${base64Data}`} />}
          </View>
          <View style={styles.infoCenter}>
            <Text style={[styles.title, styles.labelBold]}>VISITA TÉCNICA</Text>
            <Text style={[styles.name, styles.labelBold]}>
              {company.legalName}
            </Text>
          </View>
          <View style={styles.metaData}>
            <Text>Data....: {DateTime.local().toFormat('dd/MM/yyyy')}</Text>
            <Text>Hora....: {DateTime.local().toFormat('HH:mm')}</Text>
            <Text>Usuário: {username}</Text>
          </View>
        </View>
        <View style={styles.formGroup}>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Nome do Produtor:</Text>
              <Text style={styles.value}>{property.producerName}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Data da visita:</Text>
              <Text style={styles.value}>
                {endDate ? formatISODate(endDate) : '-'}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Propriedade:</Text>
              <Text style={styles.value}>{property.name}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Município:</Text>
              <Text style={styles.value}>{property.municipalityName}</Text>
            </View>
          </View>
        </View>
        {status !== NO_ISSUES_STATUS && validQualityItems.length > 0 && (
          <View style={[styles.table, { marginTop: 10, marginBottom: 10 }]}>
            <View style={[styles.answerRow, { backgroundColor: '#003366' }]}>
              {validQualityItems.map((item, index) => (
                <Text
                  key={`th-${index}`}
                  style={[styles.th, { flex: 1, textAlign: 'center' }]}
                >
                  {item.DFSIGLAANALISE}
                  {item.DFUNIDADE}
                </Text>
              ))}
            </View>
            <View style={styles.answerRow}>
              {validQualityItems.map((item, index) => (
                <Text
                  key={`td-${index}`}
                  style={[styles.td, { flex: 1, textAlign: 'center' }]}
                >
                  {item.DFMEDIA !== null && item.DFMEDIA !== undefined
                    ? String(item.DFMEDIA).substring(0, 5)
                    : '-'}
                </Text>
              ))}
            </View>
          </View>
        )}

        {status === NO_ISSUES_STATUS ? (
          <View style={styles.noIssuesContainer} wrap={false}>
            <Text style={styles.noIssuesTitle}>
              Inconformidades não encontradas
            </Text>
            <Text style={styles.noIssuesText}>
              O técnico realizou a vistoria nesta propriedade e não identificou
              itens fora do padrão ou inconformidades técnicas nesta data.
            </Text>
          </View>
        ) : (
          answers.map((answer, index) => (
            <View key={index} style={styles.answerScope}>
              <Text style={styles.sectionTitle}>
                {index + 1}. {answer.questionario.descricao}
              </Text>
              <View style={styles.table}>
                {answer.itens.map((item, idx) => (
                  <View key={idx} style={styles.answerVerticalRow} wrap={false}>
                    <Text style={[styles.labelPergunta, styles.rowGray]}>
                      {removeZeroToTheLeft(item.grau1_pergunta)}.
                      {removeZeroToTheLeft(item.grau2_pergunta)}{' '}
                      {item.texto_pergunta}
                    </Text>
                    {item.observacao_pergunta && (
                      <Text
                        style={[
                          styles.valueObservacao,
                          { fontSize: 10, color: '#444' },
                        ]}
                      >
                        <Text style={[styles.labelBold]}>
                          Nota da pergunta:
                        </Text>
                        {item.observacao_pergunta}
                      </Text>
                    )}
                    <Text style={[styles.valueResposta, styles.rowWhite]}>
                      <Text style={[styles.labelBold]}>Resposta: </Text>
                      {getAnswerValue(item)}
                    </Text>
                    <Text style={[styles.valueObservacao]}>
                      <Text style={[styles.labelBold]}>Observação: </Text>
                      {item.observacao || 'Nenhuma observação informada.'}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          ))
        )}
        {technicalRecommendation && (
          <View style={{ marginTop: 10 }} wrap={false}>
            <Text style={styles.sectionTitle}>Recomendações:</Text>
            <Text style={{ fontSize: 12, padding: 5 }}>
              {technicalRecommendation}
            </Text>
          </View>
        )}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-around',
            marginTop: 30,
            width: '100%',
          }}
          wrap={false}
        >
          <View style={{ alignItems: 'center', width: '40%' }}>
            <View style={{ height: 40, width: 80, marginBottom: 5 }}>
              {assinatura_produtor && (
                <Image
                  style={{ width: '100%', height: '100%' }}
                  src={`${assinatura_produtor}`}
                />
              )}
            </View>
            <Text style={{ fontSize: 9 }}>___________________________</Text>
            <Text style={{ fontSize: 9, marginTop: 2 }}>
              Produtor / Responsável
            </Text>
          </View>
          <View style={{ alignItems: 'center', width: '40%' }}>
            <View style={{ height: 40, width: 80, marginBottom: 5 }}>
              {assinatura_tecnico && (
                <Image
                  style={{ width: '100%', height: '100%' }}
                  src={`${assinatura_tecnico}`}
                />
              )}
            </View>
            <Text style={{ fontSize: 9 }}>___________________________</Text>
            <Text style={{ fontSize: 9, marginTop: 2 }}>Técnico</Text>
          </View>
        </View>
        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Página ${pageNumber} de ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
